import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import YouTubeIcon from '@mui/icons-material/YouTube';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useSnackbar } from 'notistack';
import '../../utils/quillBlots';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  fetchAnnouncementEventById,
  createAnnouncementEvent,
  updateAnnouncementEvent,
  clearCurrent,
} from '../../store/slices/announcementEventSlice';
import * as announcementEventApi from '../../api/announcementEventApi';
import { getEmbedUrl } from '../../utils/youtubeEmbed';
import { getTypeLabel } from '../../utils/announcementEventUtils';

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'image',
  'video',
  'align',
  'blockquote',
  'color',
  'background',
];

export default function AnnouncementEventForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.auth);
  const { current: item, loading } = useSelector((s) => s.announcementEvent);
  const quillRef = useRef(null);
  const headerInputRef = useRef(null);
  const pendingInlineRef = useRef(new Map());
  const headlineBlobUrlRef = useRef(null);

  const [form, setForm] = useState({
    type: 'announcement',
    title: '',
    body: '',
    headlineImage: null,
    startDate: '',
    endDate: '',
    place: '',
    attachments: [],
  });
  const [headlineFile, setHeadlineFile] = useState(null);
  const [headlinePreviewUrl, setHeadlinePreviewUrl] = useState(null);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);
  const [docUploading, setDocUploading] = useState(false);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image', 'video'],
          [{ align: [] }],
          ['blockquote'],
          [{ color: [] }, { background: [] }],
          ['clean'],
        ],
        handlers: {
          image: function imageHandler() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) return;
              const blobUrl = URL.createObjectURL(file);
              pendingInlineRef.current.set(blobUrl, { file, type: 'image' });
              const quill = quillRef.current?.getEditor?.();
              if (quill) {
                const range = quill.getSelection(true) || { index: quill.getLength() };
                quill.insertEmbed(range.index, 'image', blobUrl);
                quill.setSelection(range.index + 1);
              }
            };
          },
          video: function videoHandler() {
            setEmbedUrl('');
            setEmbedDialogOpen(true);
          },
        },
      },
    }),
    []
  );

  useEffect(() => {
    if (id) dispatch(fetchAnnouncementEventById(id));
    return () => {
      dispatch(clearCurrent());
      if (headlineBlobUrlRef.current) URL.revokeObjectURL(headlineBlobUrlRef.current);
      pendingInlineRef.current.forEach((_, url) => URL.revokeObjectURL(url));
      pendingInlineRef.current.clear();
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (item && isEdit) {
      setForm({
        type: item.type || 'announcement',
        title: item.title || '',
        body: item.body || '',
        headlineImage: item.headlineImage || null,
        startDate: item.startDate ? new Date(item.startDate).toISOString().slice(0, 10) : '',
        endDate: item.endDate ? new Date(item.endDate).toISOString().slice(0, 10) : '',
        place: item.place || '',
        attachments: item.attachments || [],
      });
    }
  }, [item, isEdit]);

  const handleHeaderMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (headlineBlobUrlRef.current) URL.revokeObjectURL(headlineBlobUrlRef.current);
    const blobUrl = URL.createObjectURL(file);
    headlineBlobUrlRef.current = blobUrl;
    setHeadlinePreviewUrl(blobUrl);
    setHeadlineFile(file);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const handleRemoveHeaderMedia = () => {
    if (headlineBlobUrlRef.current) URL.revokeObjectURL(headlineBlobUrlRef.current);
    headlineBlobUrlRef.current = null;
    setHeadlinePreviewUrl(null);
    setHeadlineFile(null);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const handleEmbedVideo = useCallback(() => {
    const embedSrc = getEmbedUrl(embedUrl);
    if (embedSrc) {
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, 'video', embedSrc);
        quill.setSelection(range.index + 1);
      }
      setEmbedDialogOpen(false);
      setEmbedUrl('');
      enqueueSnackbar('Vidéo intégrée.', { variant: 'success' });
    } else {
      enqueueSnackbar('URL non valide. Utilisez un lien YouTube ou Vimeo.', { variant: 'error' });
    }
  }, [embedUrl, enqueueSnackbar]);

  const uploadAllPendingMedia = async (bodyHtml) => {
    const pending = pendingInlineRef.current;
    const blobUrls = Array.from(pending.keys()).filter((url) => bodyHtml.includes(url));
    let finalHeadline = form.headlineImage;
    if (blobUrls.length === 0 && !headlineFile) return { body: bodyHtml, headlineImage: finalHeadline };

    let newBody = bodyHtml;
    const total = blobUrls.length + (headlineFile ? 1 : 0);
    let done = 0;

    if (headlineFile) {
      setUploadStatus({ phase: 'headline', percent: 0 });
      const result = await announcementEventApi.uploadAnnouncementImage(
        headlineFile,
        'headline',
        id || 'temp'
      );
      done++;
      finalHeadline = {
        url: result.url,
        publicId: result.publicId,
        resourceType: result.resourceType || 'image',
      };
      setHeadlineFile(null);
      if (headlineBlobUrlRef.current) {
        URL.revokeObjectURL(headlineBlobUrlRef.current);
        headlineBlobUrlRef.current = null;
      }
      setHeadlinePreviewUrl(null);
    }

    for (let i = 0; i < blobUrls.length; i++) {
      const blobUrl = blobUrls[i];
      const entry = pending.get(blobUrl);
      if (!entry?.file) continue;
      setUploadStatus({ phase: 'inline', current: i, total: blobUrls.length, percent: 50 });
      const result = await announcementEventApi.uploadAnnouncementImage(entry.file, 'inline', id || 'temp');
      const escaped = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      newBody = newBody.replace(new RegExp(escaped, 'g'), result.url);
      pending.delete(blobUrl);
      URL.revokeObjectURL(blobUrl);
    }
    setUploadStatus(null);
    return { body: newBody, headlineImage: finalHeadline };
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(true);
    try {
      const result = await announcementEventApi.uploadAnnouncementDocument(file);
      setForm((f) => ({
        ...f,
        attachments: [
          ...f.attachments,
          {
            url: result.url,
            publicId: result.publicId,
            filename: result.filename || file.name,
            mimetype: result.mimetype || file.type,
          },
        ],
      }));
      enqueueSnackbar('Pièce jointe ajoutée.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de l\'envoi.', { variant: 'error' });
    } finally {
      setDocUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setForm((f) => ({
      ...f,
      attachments: f.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadStatus({ phase: 'preparing', percent: 0 });
    try {
      const { body: finalBody, headlineImage: finalHeadline } = await uploadAllPendingMedia(form.body);
      setUploadStatus(null);
      const payload = {
        type: form.type,
        title: form.title,
        body: finalBody,
        startDate: form.startDate,
        endDate: form.endDate,
        place: form.place || undefined,
        attachments: form.attachments,
        ...(finalHeadline && { headlineImage: finalHeadline }),
      };
      if (isEdit) {
        const result = await dispatch(updateAnnouncementEvent({ id, ...payload }));
        if (updateAnnouncementEvent.fulfilled.match(result)) {
          enqueueSnackbar('Annonce ou événement mis à jour.', { variant: 'success' });
          navigate('/dashboard/announcement-events');
        } else {
          enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
        }
      } else {
        const result = await dispatch(createAnnouncementEvent(payload));
        if (createAnnouncementEvent.fulfilled.match(result)) {
          enqueueSnackbar('Annonce ou événement créé.', { variant: 'success' });
          navigate('/dashboard/announcement-events');
        } else {
          enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
        }
      }
    } catch (err) {
      setUploadStatus(null);
      enqueueSnackbar(err?.response?.data?.message || 'Erreur lors de l\'envoi.', { variant: 'error' });
    }
  };

  const allowed = ['admin', 'moderator', 'space-student'].includes(user?.role);
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const headerPreviewUrl = headlinePreviewUrl || form.headlineImage?.url;

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
        <IconButton onClick={() => navigate('/dashboard/announcement-events')} disabled={!!uploadStatus} aria-label="Retour">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isEdit ? 'Modifier' : 'Créer'} une annonce ou un événement
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl component="fieldset">
              <FormLabel>Type</FormLabel>
              <RadioGroup
                row
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <FormControlLabel value="announcement" control={<Radio />} label={getTypeLabel('announcement')} />
                <FormControlLabel value="event" control={<Radio />} label={getTypeLabel('event')} />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label="Titre"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label={form.type === 'event' ? 'Date de début' : 'Date de publication'}
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ minWidth: 180 }}
              />
              <TextField
                label={form.type === 'event' ? 'Date de fin' : 'Date limite'}
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ minWidth: 180 }}
              />
            </Box>

            <TextField
              fullWidth
              label="Lieu (optionnel)"
              value={form.place}
              onChange={(e) => setForm((f) => ({ ...f, place: e.target.value }))}
            />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Image d'en-tête
              </Typography>
              <input
                ref={headerInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleHeaderMediaChange}
                style={{ display: 'none' }}
              />
              {headerPreviewUrl ? (
                <Box sx={{ position: 'relative', maxWidth: 500 }}>
                  <Box
                    component="img"
                    src={headerPreviewUrl}
                    alt="En-tête"
                    sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 2, display: 'block' }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveHeaderMedia}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => headerInputRef.current?.click()}
                  disabled={!!uploadStatus}
                  size="small"
                >
                  Choisir une image
                </Button>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Contenu
              </Typography>
              <Box sx={{ '& .quill': { bgcolor: 'background.paper' }, '& .ql-editor': { minHeight: 200 } }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.body}
                  onChange={(v) => setForm((f) => ({ ...f, body: v }))}
                  modules={modules}
                  formats={formats}
                  placeholder="Rédigez le contenu…"
                  readOnly={!!uploadStatus}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Pièces jointes (PDF, Word)
              </Typography>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
                id="announcement-doc-upload"
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="announcement-doc-upload"
                startIcon={docUploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
                disabled={docUploading || !!uploadStatus}
                size="small"
              >
                Ajouter un fichier
              </Button>
              {form.attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {form.attachments.map((att, idx) => (
                    <Box
                      key={att.publicId || idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <AttachFileIcon fontSize="small" />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {att.filename || 'Document'}
                      </Typography>
                      <IconButton size="small" onClick={() => removeAttachment(idx)} disabled={!!uploadStatus}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {uploadStatus && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {uploadStatus.phase === 'preparing'
                    ? "Préparation de l'envoi…"
                    : uploadStatus.phase === 'headline'
                    ? `Envoi de l'image…`
                    : "Envoi des médias…"}
                </Typography>
                <LinearProgress sx={{ height: 6, borderRadius: 1 }} />
              </Paper>
            )}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !!uploadStatus || !form.title || !form.startDate || !form.endDate || !form.body}
            >
              {loading && !uploadStatus ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isEdit ? 'Enregistrer' : 'Publier'
              )}
            </Button>
            <Button onClick={() => navigate('/dashboard/announcement-events')} disabled={!!uploadStatus}>
              Annuler
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog open={embedDialogOpen} onClose={() => setEmbedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insérer une vidéo YouTube / Vimeo</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="URL de la vidéo"
            placeholder="https://www.youtube.com/watch?v=..."
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleEmbedVideo} startIcon={<YouTubeIcon />}>
            Insérer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
